import React, { createContext, useContext, useEffect, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { useIsMobile } from "../../../../helper/hooks/useIsMobile.js";
import { ComponentType } from "../../types.js";
import { jsx as _jsx } from "react/jsx-runtime";
export const FormContext = /*#__PURE__*/createContext(null);
// TODO: pass generic consequently throughout formRenderer
export function FormContextProvider({
  onSubmit,
  formConfiguration,
  useSmallFields = false,
  isFormDisabled = false,
  isLoading = false,
  disableOnLoading = false,
  scrollsToError = false,
  children,
  ...reactHookFormProps
}) {
  const isMobile = useIsMobile();
  const defaultValues = useMemo(() => {
    return formConfiguration.reduce((acc, config) => {
      if (!config.id) {
        return acc;
      }
      if ('componentProps' in config && config.component === ComponentType.GRID && 'children' in config.componentProps) {
        const {
          children
        } = config.componentProps;
        if (!children) {
          return acc;
        }
        const childrenAsArray = Array.isArray(children) ? children : [children];
        childrenAsArray.forEach(child => {
          if (isValidChild(child)) {
            acc[child.id] = child.componentProps.value;
          }
        });
        return acc;
      }
      if (acc[config.id] || !('componentProps' in config) || config.componentProps && !('value' in config.componentProps) || !config.componentProps?.value) {
        return acc;
      }
      return {
        ...acc,
        [config.id]: config.componentProps.value
      };
    }, reactHookFormProps.defaultValues ?? {});
  }, [formConfiguration, reactHookFormProps.defaultValues]);
  const reactHookFormValues = useForm({
    ...reactHookFormProps,
    defaultValues
  });
  useEffect(() => {
    reactHookFormValues.reset(defaultValues, {
      keepErrors: true,
      keepDefaultValues: true
    });
  }, [defaultValues, reactHookFormProps.defaultValues, reactHookFormValues]);
  const formIsDisabled = isLoading && disableOnLoading || isFormDisabled;
  const {
    formState: {
      submitCount,
      errors
    }
  } = reactHookFormValues;
  useEffect(() => {
    const errorKeys = Object.keys(errors);
    if (scrollsToError && submitCount > 0 && errorKeys.length > 0 &&
    // Workaround for scrolling to the first error if the field was not visible on first render
    document.querySelector('div[class*="error"]')) {
      document.querySelector('div[class*="error"]')?.scrollIntoView(true);
      window.scrollBy({
        top: isMobile ? -20 : -70,
        behavior: 'smooth'
      });
    }
  }, [submitCount, errors, scrollsToError, isMobile]);
  return /*#__PURE__*/_jsx(FormContext.Provider, {
    value: {
      ...reactHookFormValues,
      formConfiguration,
      useSmallFields,
      isLoading,
      scrollsToError,
      isFormDisabled: formIsDisabled
    },
    children: /*#__PURE__*/_jsx("form", {
      onSubmit: reactHookFormValues.handleSubmit(onSubmit),
      children: children
    })
  });
}
export function useFormContext() {
  const context = useContext(FormContext);
  if (!context) {
    throw new Error('useFormContext() must be called within a Provider');
  }
  return context;
}
function isValidChild(child) {
  return typeof child === 'object' && child?.componentProps?.value && child?.id;
}